import * as Immutable from "immutable";
import * as redux from "redux";
import { SimpleActionCreator, createAction, createReducer } from "redux-act";
import { combineReducers } from "redux-immutable";
import { PickLiteral, NestedKeyOf } from "./ReducerTypes";
import { MappedUserInfo } from "Shared/models/MappedUserInfo";
import { Country } from "Components/searchbox/searchTab/flights/RoutePickerModel";
import { NavitaireSettings } from "ComponentHelpers/useNavitaireSettings";
import { KenticoKontent } from "Components/main-menu/KenticoKontent";
import { CaptchaStatus } from "Components/main-menu/useAppInitializer";
import {
	CurrentStatusSearch,
	DEFAULT_CURRENT_STATUS_SEARCH,
} from "Components/searchbox/flightStatusTab/CurrentStatusSearch";


export class ReduxContext {
	public static store: redux.Store<{[x: string]: any}>;

	public static dispatch(action: redux.Action) {
		ReduxContext.store.dispatch(action);
	}

	public static registerReducer(key: string, reducer: redux.Reducer<Immutable.Map<any, any>>) {
		ReduxContext.reducerRegistry[key] = reducer;
	}

	public static createStore() {
		const w: any = window as any;
		const rootReducer = combineReducers(ReduxContext.reducerRegistry);
		ReduxContext.store = redux.createStore(
			rootReducer,
			w.__REDUX_DEVTOOLS_EXTENSION__ && w.__REDUX_DEVTOOLS_EXTENSION__()
		);
	}

	private static readonly reducerRegistry: redux.ReducersMapObject = {};
}

export const REDUCER_NAME = "js-reducer";

export interface StateMap {
	countries: Country[];
	currentStatusSearch: CurrentStatusSearch;
	captchaStatus: CaptchaStatus;
	kontent: KenticoKontent;
	navitaireSettings: NavitaireSettings;
	userInfo: MappedUserInfo;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ReducerAction =
	| {
			action: SimpleActionCreator<StateMap["countries"]>;
			initialValue: StateMap["countries"];
			propertyName: PickLiteral<NestedKeyOf<StateMap>, "countries">;
	  }
	| {
			action: SimpleActionCreator<StateMap["currentStatusSearch"]>;
			initialValue: StateMap["currentStatusSearch"];
			propertyName: PickLiteral<NestedKeyOf<StateMap>, "currentStatusSearch">;
	  }
	| {
			action: SimpleActionCreator<StateMap["captchaStatus"]>;
			initialValue: StateMap["captchaStatus"];
			propertyName: PickLiteral<NestedKeyOf<StateMap>, "captchaStatus">;
	  }
	| {
			action: SimpleActionCreator<StateMap["kontent"]>;
			initialValue: StateMap["kontent"];
			propertyName: PickLiteral<NestedKeyOf<StateMap>, "kontent">;
	  }
	| {
			action: SimpleActionCreator<StateMap["navitaireSettings"]>;
			initialValue: StateMap["navitaireSettings"];
			propertyName: PickLiteral<NestedKeyOf<StateMap>, "navitaireSettings">;
	  }
	| {
			action: SimpleActionCreator<StateMap["userInfo"]>;
			initialValue: StateMap["userInfo"];
			propertyName: PickLiteral<NestedKeyOf<StateMap>, "userInfo">;
	  };

const addPropertyByDotNotation = (object: any, pathName: string, value: any) => {
	const path = pathName.split(".");
	const last = path.pop();
	const lastObj = path.reduce((obj, key) => (obj[key] = obj[key] || {}), object);
	lastObj[last] = value;

	return object;
};

export const reducerActions: ReducerAction[] = [
	{
		action: createAction<Country[]>("countries"),
		initialValue: [],
		propertyName: "countries",
	},
	{
		action: createAction<CurrentStatusSearch>("currentStatusSearch"),
		initialValue: DEFAULT_CURRENT_STATUS_SEARCH,
		propertyName: "currentStatusSearch",
	},
	{
		action: createAction<CaptchaStatus>("captchaStatus"),
		initialValue: "loading",
		propertyName: "captchaStatus",
	},
	{
		action: createAction<KenticoKontent>("kontent"),
		initialValue: undefined,
		propertyName: "kontent",
	},
	{
		action: createAction<NavitaireSettings>("navitaireSettings"),
		initialValue: undefined,
		propertyName: "navitaireSettings",
	},
	{
		action: createAction<MappedUserInfo>("userInfo"),
		initialValue: undefined,
		propertyName: "userInfo",
	},
];

const mappedActions = reducerActions.reduce(
	(actions, { propertyName, action }) => ({
		...actions,
		[action.toString()]: (state: Immutable.Map<any, any>, data: any) =>
			state.updateIn([...propertyName.split(".")], (_) => Immutable.fromJS(data)),
	}),
	{} as any
);

const mappedInitialState: StateMap = reducerActions.reduce(
	(state, { propertyName, initialValue }) => addPropertyByDotNotation(state, propertyName, initialValue),
	{} as StateMap
);

ReduxContext.registerReducer(
	REDUCER_NAME,
	createReducer<Immutable.Map<any, any>>(mappedActions, Immutable.fromJS(mappedInitialState))
);

ReduxContext.createStore();
